/** @format */

import { GTMCityAndNeighborhoodsApartmentCarousel } from '@/@ducks/googleTagManager/helpers/google-tag-manager';
import { TRoomCard } from '@/@ducks/room/declarations';
import { TListType } from '@/ducks/general/declarations';
import { useGTMListType } from '@/ducks/general/hooks/useGTMListType';
import { selectorAvailabilityRoom } from '@/ducks/shared/selectors';
import { useAppTranslation } from '@/i18n/declarations';
import { utilityGTMBuildStringByListingActiveFilters } from '@/utils/helpers/utilityGTMBuildStringByListingActiveFilters';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';

import React, { useCallback } from 'react';
import { useLoader } from '../../../@ducks/bookingNew/hooks/useLoader';
import { selectorFilters } from '../../../@ducks/listing/selectors';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { utilityGTMListingSelectItem } from '../../../utils/helpers/utilityGTMListingSelectItem';
import { usePageListingContext } from '../../page/PageListing/components/PageListingContext';
import Availability from './Availability';
import { useCardRoomContext } from './context';
import Details from './Details';
import Gallery from './Gallery';
import { BottomDetails, Card, CardInfo, CardLink, Divider, Title } from './index.style';
import Price from './Price';

export const CardRoom: React.FC<CardRoomProps> = ({
  isLoadingAvailability,
  availableFrom,
  index,
  isCarousel,
  selectedFilter,
  currentPage,
  hideTopRadius = false,
  isPopup = false,
}) => {
  const pageListingContext = usePageListingContext();
  const { addLoader } = useLoader('availability');

  const { intl } = useAppTranslation();
  const dispatch = useAppDispatch();
  const { url, title, room } = useCardRoomContext();
  const { code } = room || {};
  const roomAvailability = useAppSelector(store => selectorAvailabilityRoom(store, code ?? ''));

  const filters = useAppSelector(selectorFilters);
  const activeCategoryFilter = utilityGTMBuildStringByListingActiveFilters(filters);
  const { onChangeGTMListType, roomListType } = useGTMListType();

  const listType: TListType = (() => {
    if (isPopup) {
      return 'view_on_map';
    } else if (!!currentPage) {
      return currentPage;
    } else if (isCarousel) {
      return 'related_products';
    } else if (isEmpty(activeCategoryFilter)) {
      return 'search_result';
    } else {
      return 'sorting_by_product_filters';
    }
  })();

  const gtmSelectItem = useCallback(() => {
    addLoader();
    dispatch({
      type: 'SET_ROOM_INDEX',
      payload: `${index ?? 1}`,
    });

    if (listType !== roomListType) {
      onChangeGTMListType(listType);
    }

    utilityGTMListingSelectItem(room as TRoomCard, roomAvailability, intl, index ?? 1, filters, listType);
    if (isCarousel) {
      GTMCityAndNeighborhoodsApartmentCarousel(
        snakeCase(room?.city.nameEn.toLowerCase() ?? ''),
        room?.code ?? '',
        selectedFilter ?? '',
        currentPage ?? 'city_page',
      );
    }
  }, [code, selectedFilter, listType]);

  const onMouseEnter = useCallback(() => {
    if (pageListingContext) {
      const { setHoveredRoom } = pageListingContext;
      setHoveredRoom({
        code: room?.code ?? '',
        coords: {
          lat: room?.position.latitude ?? 0,
          lng: room?.position.longitude ?? 0,
        },
      });
    }
  }, []);

  const onMouseLeave = useCallback(() => {
    if (pageListingContext) {
      const { setHoveredRoom } = pageListingContext;
      setHoveredRoom(null);
    }
  }, []);

  return (
    <Card $hideTopRadius={hideTopRadius} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <Gallery gtmSelectItem={gtmSelectItem} isPopup={isPopup} />
      <CardLink to={url} target="_blank" rel="noopener noreferrer nofollow" onClick={gtmSelectItem}>
        <CardInfo>
          <Title>{title}</Title>
          <Details isPopup={isPopup} />
          <Divider />
          <BottomDetails isPopup={isPopup}>
            {!isLoadingAvailability && <Price isPopup={isPopup} room={room} />}
            {!isPopup && (
              <Availability bedCode={code} isLoading={isLoadingAvailability} availableFrom={availableFrom} />
            )}
          </BottomDetails>
          {isPopup && (
            <Availability
              bedCode={code}
              isLoading={isLoadingAvailability}
              availableFrom={availableFrom}
              isPopup={isPopup}
            />
          )}
        </CardInfo>
      </CardLink>
    </Card>
  );
};

interface CardRoomProps {
  hasInactiveURL?: boolean;
  isLoadingAvailability: boolean;
  availableFrom: string | undefined;
  index?: number;
  isCarousel?: boolean;
  selectedFilter?: string;
  currentPage?: 'city_page' | 'neighborhoods_page';
  hideTopRadius?: boolean;
  isPopup?: boolean;
}
